/**
 * A program, and the activities under it (M1 track D4).
 *
 * The screen where an activity is created, because that is the only place the
 * program is unambiguous — §7.13 makes an activity meaningless without one, and
 * a create form floating on the activities list would need a program picker
 * that is right most of the time and wrong occasionally.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ACTIVITY_STATUSES_SETTABLE,
  formatDuration,
  PROGRAM_STATUS,
  PROGRAM_STATUS_TRANSITIONS,
} from '@parinaam-vms/shared';
import type { ActivityWithProgram, Program } from '@parinaam-vms/shared';
import {
  Button,
  Card,
  DataTable,
  DeletePanel,
  DetailList,
  DurationInput,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  SelectInput,
  StatusBadge,
  TextArea,
  TextInput,
} from '../../components/common';
import {
  useActivities,
  useCreateActivity,
  useDeleteProgram,
  useProgram,
  useUpdateProgram,
} from '../../hooks/usePrograms';
import { useAuth } from '../../hooks/useAuth';
import type { NewActivityBody, ProgramBody, ProgramDetail } from '../../services/api/programs';
import { useToast } from '../../components/common/Toast';
import { CRUMB } from '../../constants/breadcrumbs';
import {
  formatDate,
  formatDateRange,
  formatTimeRange,
  humaniseCode,
  orDash,
} from '../../utils/format';
import styles from './ProgramsPage.module.css';

export function ProgramDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [editing, setEditing] = useState(false);
  const [addingActivity, setAddingActivity] = useState(false);

  const query = useProgram(id);
  // One programme, as a list of one: the filter takes several since the
  // attendance screen's programme picker became a multi-select.
  const activities = useActivities({ program_id: id ? [id] : undefined, page_size: 100 });

  if (query.isPending) return <LoadingState label="Loading the program…" />;
  if (query.isError) {
    return (
      <>
        <PageHeader title="Program" breadcrumbs={[CRUMB.programs, { label: 'Program' }]} />
        <Card>
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </Card>
      </>
    );
  }

  const program = query.data!;

  return (
    <>
      <PageHeader
        title={program.name}
        breadcrumbs={[CRUMB.programs, { label: program.name }]}
        description={
          program.program_code
            ? `${program.program_code} · ${program.activity_count} ${program.activity_count === 1 ? 'activity' : 'activities'}`
            : `${program.activity_count} ${program.activity_count === 1 ? 'activity' : 'activities'}`
        }
        actions={
          <div className={styles.headerActions}>
            <StatusBadge status={program.status} />
            {!editing && program.status !== PROGRAM_STATUS.DELETED && (
              <Button variant="primary" onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}
          </div>
        }
      />

      {editing ? (
        <EditProgramForm program={program} onClose={() => setEditing(false)} />
      ) : (
        <Card title="Details">
          <DetailList
            items={[
              { label: 'Name', value: program.name },
              { label: 'Short code', value: orDash(program.program_code) },
              { label: 'Starts', value: formatDate(program.start_date) },
              { label: 'Ends', value: formatDate(program.end_date) },
              { label: 'Status', value: humaniseCode(program.status) },
              { label: 'Description', value: orDash(program.description), wide: true },
            ]}
          />
        </Card>
      )}

      <Card
        title="Activities"
        action={
          !addingActivity &&
          program.status !== PROGRAM_STATUS.DELETED && (
            <Button variant="primary" onClick={() => setAddingActivity(true)}>
              New activity
            </Button>
          )
        }
      >
        {addingActivity && (
          <ActivityForm programId={program.program_id} onClose={() => setAddingActivity(false)} />
        )}

        {activities.isPending ? (
          <LoadingState />
        ) : activities.isError ? (
          <ErrorState error={activities.error} onRetry={() => void activities.refetch()} />
        ) : (activities.data?.items ?? []).length === 0 ? (
          <EmptyState
            title="No activities yet"
            message="Add an activity to start assigning people."
          />
        ) : (
          <DataTable<ActivityWithProgram>
            caption="Activities in this program"
            rows={activities.data?.items ?? []}
            getRowKey={(row) => row.activity_id}
            // Stated rather than left to the fallback, which happens to agree:
            // the two call sites should read the same way, and the one that is
            // only correct by accident is the one that breaks quietly.
            onRowClick={(row) =>
              navigate(`/activities/${row.activity_id}`, { state: { from: 'program' } })
            }
            columns={[
              { key: 'name', header: 'Activity name', render: (row) => row.name },
              {
                key: 'when',
                header: 'Activity date',
                render: (row) => formatDateRange(row.start_date, row.end_date),
              },
              {
                key: 'time',
                header: 'Activity time',
                render: (row) => formatTimeRange(row.start_time, row.end_time),
              },
              {
                key: 'location',
                header: 'Activity location',
                render: (row) => orDash(row.location),
              },
              {
                key: 'hours',
                header: 'Number of hours per activity',
                numeric: true,
                render: (row) =>
                  row.planned_minutes == null ? '—' : formatDuration(row.planned_minutes),
              },
              {
                key: 'status',
                header: 'Status',
                render: (row) => <StatusBadge status={row.status} />,
              },
            ]}
          />
        )}
      </Card>

      <DeleteProgram program={program} />
    </>
  );
}

/**
 * Deleting the program — admin only, and last on the page.
 *
 * The consequences panel names the activity count, because the cascade is the
 * part nobody predicts: deleting a program takes every activity under it, and
 * with them every hour recorded against those activities. An admin who thought
 * they were tidying away an empty container needs to see the number before they
 * confirm, not in the toast afterwards.
 */
function DeleteProgram({ program }: { program: ProgramDetail }) {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const mutation = useDeleteProgram(program.program_id);

  if (!hasRole('ADMIN')) return null;

  const count = program.activity_count;

  return (
    <DeletePanel
      title="Delete this program"
      noun="program"
      deleted={program.status === PROGRAM_STATUS.DELETED}
      pending={mutation.isPending}
      error={mutation.isError ? (mutation.error as Error) : null}
      // Back to the list: staying on a screen whose record has just been deleted
      // leaves the admin looking at something that no longer applies.
      onDelete={(reason) => mutation.mutate(reason, { onSuccess: () => navigate('/programs') })}
      consequences={
        <>
          <p>
            <strong>This cannot be undone.</strong> {program.name} will be deleted permanently.{' '}
            {count === 0 ? 'Any activities under it' : null}
            {count === 1 ? 'Its 1 activity' : null}
            {count > 1 ? `All ${count} of its activities` : null} will be cancelled, and the
            programme cannot be edited or reinstated afterwards.
          </p>
          <p>
            Every session beneath them is <strong>cancelled</strong>, so nobody can still be
            assigned to one and no volunteer is offered a place on work that is not happening.
            Sessions that already took place are left as they are — they are a record of work real
            people did.
          </p>
          <p>
            Every hour recorded against those activities stays in the record but{' '}
            <strong>stops counting towards the dashboard</strong>, including total volunteer hours.
            If the program has simply finished, mark it completed or inactive instead — both
            reverse, and both keep the hours.
          </p>
        </>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Forms
// ---------------------------------------------------------------------------

function EditProgramForm({
  program,
  onClose,
}: {
  // `Program`, not `ProgramBody & { program_id }`. The old type was a Partial, so
  // every field read here was optional even though the call site has always
  // passed a loaded program — and the status dropdown needs a real status to
  // work out where it may go.
  program: Program;
  onClose: () => void;
}) {
  const mutation = useUpdateProgram(program.program_id);
  const toast = useToast();
  const [form, setForm] = useState({
    name: program.name ?? '',
    program_code: program.program_code ?? '',
    description: program.description ?? '',
    status: program.status ?? 'DRAFT',
    start_date: program.start_date ?? '',
    end_date: program.end_date ?? '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (field: string) => (value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: '' }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (form.name.trim() === '') {
      setErrors({ name: 'A program needs a name.' });
      return;
    }
    if (form.start_date && form.end_date && form.start_date > form.end_date) {
      setErrors({ end_date: 'The end date cannot be before the start date.' });
      return;
    }

    // Only what changed, for the same reason as the volunteer edit: PATCH treats
    // an absent key as "leave alone" and a null as "clear".
    const next: Record<string, string | null> = {
      name: form.name.trim(),
      program_code: form.program_code.trim() || null,
      description: form.description.trim() || null,
      status: form.status,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
    };

    const changes: ProgramBody = {};
    for (const [key, value] of Object.entries(next)) {
      // Via `unknown`: an interface will not assign to an index signature however
      // identical the shape — the same wrinkle noted in services/api/client.ts.
      // It only surfaced here once the prop stopped being a Partial.
      const stored = (program as unknown as Record<string, unknown>)[key] ?? null;
      if (value !== stored) (changes as Record<string, unknown>)[key] = value;
    }

    if (Object.keys(changes).length === 0) {
      toast.info('No changes to save.');
      onClose();
      return;
    }

    mutation.mutate(changes, { onSuccess: onClose });
  };

  return (
    <Card title="Edit program">
      <form onSubmit={handleSubmit} noValidate>
        {mutation.isError && (
          <p className={styles.error} role="alert">
            {(mutation.error as Error).message}
          </p>
        )}

        <fieldset className={styles.section} disabled={mutation.isPending}>
          <div className={styles.row}>
            <TextInput
              label="Name"
              required
              value={form.name}
              error={errors.name}
              onChange={(event) => set('name')(event.target.value)}
            />
            <TextInput
              label="Short code"
              value={form.program_code}
              onChange={(event) => set('program_code')(event.target.value)}
            />
          </div>

          <div className={styles.row}>
            <TextInput
              label="Start date"
              type="date"
              value={form.start_date}
              error={errors.start_date}
              onChange={(event) => set('start_date')(event.target.value)}
            />
            <TextInput
              label="End date"
              type="date"
              value={form.end_date}
              error={errors.end_date}
              onChange={(event) => set('end_date')(event.target.value)}
            />
          </div>

          <SelectInput
            label="Status"
            placeholder="Select…"
            // Where this program can go from where it is (Section 8.43).
            options={[program.status, ...PROGRAM_STATUS_TRANSITIONS[program.status]].map(
              (status) => ({
                value: status,
                label: humaniseCode(status),
              })
            )}
            value={form.status}
            onChange={(event) => set('status')(event.target.value)}
          />

          <TextArea
            label="Description"
            rows={3}
            value={form.description}
            onChange={(event) => set('description')(event.target.value)}
          />
        </fieldset>

        <div className={styles.actions}>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            Save changes
          </Button>
          <Button onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

function ActivityForm({ programId, onClose }: { programId: string; onClose: () => void }) {
  const mutation = useCreateActivity();
  const EMPTY = {
    name: '',
    activity_code: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    location: '',
    status: 'SCHEDULED',
    description: '',
    volunteers_needed: '',
  };
  const [form, setForm] = useState(EMPTY);
  /*
   * Minutes, and held apart from `form` because everything in there is a
   * string from a text input. DurationInput speaks minutes both ways, which is
   * the unit the column stores and the unit the attendance form it pre-fills
   * expects - so no conversion exists anywhere to get wrong.
   */
  const [defaultMinutes, setDefaultMinutes] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (field: string) => (value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: '' }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const problems: Record<string, string> = {};
    // Everything is required here (17 Sep 2026): an activity with no location
    // or no times is one nobody can be scheduled against.
    if (form.name.trim() === '') problems.name = 'An activity needs a name.';
    if (form.start_date === '') problems.start_date = 'When does this activity start?';
    if (form.end_date === '') problems.end_date = 'When does it finish?';
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      problems.end_date = 'The end date must be on or after the start date.';
    }
    if (form.start_time && form.end_time && form.end_time <= form.start_time) {
      problems.end_time = 'The end time must be after the start time.';
    }
    if (form.location.trim() === '') problems.location = 'Where does it happen?';
    if (form.status === '') problems.status = 'Choose a status.';
    if (form.description.trim() === '') {
      problems.description = 'Say what happens in this activity.';
    }
    // Required like every other field on this form (17 Sep 2026). Staff know
    // how long their own activity runs, and the figure saves retyping it for
    // every volunteer at every session.
    if (defaultMinutes <= 0) {
      problems.default_duration_minutes = 'How long is a volunteer normally here?';
    }
    if (
      form.volunteers_needed &&
      (!/^\d+$/.test(form.volunteers_needed) || Number(form.volunteers_needed) < 1)
    ) {
      problems.volunteers_needed = 'Enter a whole number of at least 1, or leave it blank.';
    }
    if (Object.keys(problems).length > 0) {
      setErrors(problems);
      return;
    }

    const body: NewActivityBody = {
      program_id: programId,
      name: form.name.trim(),
      activity_code: form.activity_code.trim() || null,
      description: form.description.trim(),
      start_date: form.start_date,
      end_date: form.end_date,
      start_time: form.start_time || null,
      end_time: form.end_time || null,
      location: form.location.trim(),
      status: form.status as NewActivityBody['status'],
      default_duration_minutes: defaultMinutes,
      volunteers_needed: form.volunteers_needed ? Number(form.volunteers_needed) : null,
    };

    mutation.mutate(body, {
      onSuccess: () => {
        setForm(EMPTY);
        setDefaultMinutes(0);
        onClose();
      },
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className={styles.section}>
      {mutation.isError && (
        <p className={styles.error} role="alert">
          {(mutation.error as Error).message}
        </p>
      )}

      <fieldset className={styles.section} disabled={mutation.isPending}>
        <div className={styles.row}>
          <TextInput
            label="Activity name"
            required
            value={form.name}
            error={errors.name}
            onChange={(event) => set('name')(event.target.value)}
          />
          <TextInput
            label="Activity short code"
            value={form.activity_code}
            onChange={(event) => set('activity_code')(event.target.value)}
          />
        </div>

        <div className={styles.row}>
          <TextInput
            label="Activity start date"
            type="date"
            required
            value={form.start_date}
            error={errors.start_date}
            onChange={(event) => set('start_date')(event.target.value)}
          />
          <TextInput
            label="Activity end date"
            type="date"
            required
            value={form.end_date}
            error={errors.end_date}
            onChange={(event) => set('end_date')(event.target.value)}
          />
        </div>

        <div className={styles.row}>
          <TextInput
            label="Activity start time"
            type="time"
            value={form.start_time}
            error={errors.start_time}
            onChange={(event) => set('start_time')(event.target.value)}
          />
          <TextInput
            label="Activity end time"
            type="time"
            value={form.end_time}
            error={errors.end_time}
            onChange={(event) => set('end_time')(event.target.value)}
          />
        </div>

        <div className={styles.row}>
          <DurationInput
            label="How long is a volunteer normally here?"
            required
            value={defaultMinutes}
            error={errors.default_duration_minutes}
            onChange={(value) => {
              setDefaultMinutes(value);
              setErrors((current) => ({ ...current, default_duration_minutes: '' }));
            }}
          />
        </div>

        <div className={styles.row}>
          <TextInput
            label="Activity location"
            required
            value={form.location}
            error={errors.location}
            onChange={(event) => set('location')(event.target.value)}
          />
          <SelectInput
            label="Status"
            required
            placeholder="Select…"
            options={ACTIVITY_STATUSES_SETTABLE.map((status) => ({
              value: status,
              label: humaniseCode(status),
            }))}
            value={form.status}
            error={errors.status}
            onChange={(event) => set('status')(event.target.value)}
          />
        </div>

        <TextArea
          label="Activity description"
          required
          rows={2}
          value={form.description}
          error={errors.description}
          onChange={(event) => set('description')(event.target.value)}
        />

        <TextInput
          label="Volunteers needed"
          type="number"
          min={1}
          placeholder="Leave blank for no limit"
          value={form.volunteers_needed}
          error={errors.volunteers_needed}
          onChange={(event) => set('volunteers_needed')(event.target.value)}
        />
      </fieldset>

      <div className={styles.actions}>
        <Button type="submit" variant="primary" loading={mutation.isPending}>
          Add activity
        </Button>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
